import React, { useState, useEffect } from 'react';
import { useFeatureService } from './services/useFeatureService';
import type { DynamicFieldRule } from '../core/types/DynamicEntity.types';
import { FieldDesigner } from './FieldDesigner';
import { FieldEditor } from './FieldEditor';
import { RuleEditor } from './RuleEditor';
import { LayoutContent } from '../../LayoutContent';
import {
    useDynamicEntitiesContext,
    useDynamicEntitiesFetcher,
    useDynamicEntitiesPermissions
} from '../core/DynamicEntitiesProvider';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { AlertDialog } from '../../AlertDialog/AlertDialog';

interface EntityEditorProps {
    entityId: string;
    featureId?: string;
    featureEntityId?: string;
    initialRenderMode?: 'tab' | 'inline' | 'modal';
    onBack: () => void;
}

export const EntityEditor: React.FC<EntityEditorProps> = ({
    entityId,
    featureId,
    featureEntityId,
    initialRenderMode,
    onBack
}) => {
    const { baseUrl, onError } = useDynamicEntitiesContext();
    const http = useDynamicEntitiesFetcher();
    const { canUpdate } = useDynamicEntitiesPermissions();
    const service = useFeatureService();
    const [entity, setEntity] = useState<any | null>(null);
    const [fields, setFields] = useState<any[]>([]);
    const [rules, setRules] = useState<DynamicFieldRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingField, setEditingField] = useState<any | null>(null);
    const [activeTab, setActiveTab] = useState<'fields' | 'rules'>('fields');
    const [renderMode, setRenderMode] = useState<'tab' | 'inline' | 'modal'>(initialRenderMode || 'tab');
    const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean, config: Partial<React.ComponentProps<typeof AlertDialog>> & { title?: string } }>({ isOpen: false, config: {} });

    const loadEntity = () => {
        http(`${baseUrl}/dynamic-entities/${entityId}`)
            .then(res => {
                if (!res.ok) throw new Error(`Failed to fetch entity: ${res.status} ${res.statusText}`);
                return res.json();
            })
            .then(data => {
                setEntity(data);
                setFields(data.fields || []);
                setRules(data.rules || []);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                onError?.(err as Error);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadEntity();
    }, [entityId, baseUrl, http]);

    const handleSaveEntity = async () => {
        if (!entity) return;
        setLoading(true);
        try {
            // 1. Update entity properties
            await service.updateEntity(entityId, {
                id: entityId,
                code: entity.entityCode, // The DTO uses entityCode, not code
                multiRecord: entity.isPlansAndCriteria ? false : entity.multiRecord,
                description: entity.description,
                isReadonly: entity.isReadonly,
                isDisabled: entity.isDisabled,
                isPlansAndCriteria: entity.isPlansAndCriteria
            });

            // 2. Update fields
            await service.saveFields(entityId, fields);

            // 3. Update rules
            await service.saveRules(entityId, rules);

            // 4. Update FeatureEntity link (RenderMode) if applicable
            if (featureId && featureEntityId) {
                await service.updateFeatureEntity(featureId, entityId, {
                    id: featureEntityId,
                    featureId: featureId,
                    dynamicEntityId: entityId,
                    renderMode: renderMode,
                    order: 0, // We don't change order here, backend should ignore or keep it
                });
            }

            setAlertConfig({
                isOpen: true,
                config: {
                    title: '¡Éxito!',
                    cancelText: 'Cerrar',
                    onCancel: () => setAlertConfig({ isOpen: false, config: {} }),
                    onConfirm: undefined, // No primary button needed
                }
            });
            loadEntity();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            setAlertConfig({
                isOpen: true,
                config: {
                    title: 'Error',
                    description: 'Hubo un error al guardar.',
                    cancelText: 'Cerrar',
                    onCancel: () => setAlertConfig({ isOpen: false, config: {} }),
                    onConfirm: undefined,
                }
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-6 text-gray-500">Cargando...</div>;
    if (!entity) return <div className="p-6 text-red-500">Entidad no encontrada</div>;

    const isSystem = entity.isSystem === true;
    // Sin permiso de escritura la pantalla se comporta como una entidad de
    // sistema: se puede consultar, no modificar.
    const isReadonly = isSystem || !canUpdate;

    const saveAction = !isReadonly ? {
        label: "Guardar Cambios",
        onClick: handleSaveEntity,
        icon: <CheckIcon className="w-4 h-4" />,
        size: "sm" as const
    } : undefined;

    return (
        <LayoutContent
            title={entity.description || entity.entityCode}
            onBack={onBack}
            primaryAction={saveAction}
        >
            <div className="flex flex-col gap-6 w-full">

                {/* Banner entidad de sistema */}
                {isSystem && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3">
                        <LockClosedIcon className="w-5 h-5 text-amber-600 shrink-0" />
                        <p className="text-sm text-amber-700">
                            Esta es una entidad de sistema. Su configuración es de solo lectura y no puede modificarse.
                        </p>
                    </div>
                )}

                {/* Banner sin permiso de escritura */}
                {!isSystem && !canUpdate && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3">
                        <LockClosedIcon className="w-5 h-5 text-amber-600 shrink-0" />
                        <p className="text-sm text-amber-700">
                            No tienes permiso para modificar esta entidad. Puedes consultarla en modo lectura.
                        </p>
                    </div>
                )}

                {/* Configuration Options Panel */}
                <div className="bg-white rounded-2xl p-5 flex items-start w-full">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full items-end">

                        {entity.isPlansAndCriteria && (
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-gray-900">Código</label>
                                <input
                                    type="text"
                                    value={entity.entityCode || ''}
                                    onChange={(e) => setEntity({ ...entity, entityCode: e.target.value })}
                                    disabled={isReadonly}
                                    className="px-2 h-8 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0e79fd] focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                                />
                            </div>
                        )}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-900">Nombre</label>
                            <input
                                type="text"
                                value={entity.description || ''}
                                onChange={(e) => setEntity({ ...entity, description: e.target.value })}
                                disabled={isReadonly}
                                className="px-2 h-8 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0e79fd] focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            />
                        </div>

                        {/* Multi registro — oculto temporalmente
                        <div className={`flex gap-3 items-start ${(entity.isPlansAndCriteria || isSystem) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                            onClick={() => !entity.isPlansAndCriteria && !isSystem && setEntity({ ...entity, multiRecord: !entity.multiRecord })}>
                            <div className="mt-0.5">
                                <div className={`w-8 h-5 rounded-full relative transition-colors duration-200 ease-in-out ${(entity.isPlansAndCriteria ? false : entity.multiRecord) ? 'bg-[#0e79fd]' : 'bg-gray-200'}`}>
                                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ease-in-out shadow-sm ${(entity.isPlansAndCriteria ? false : entity.multiRecord) ? 'transform translate-x-3' : ''}`} />
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <span className={`text-sm font-medium ${(entity.isPlansAndCriteria || isSystem) ? 'text-gray-500' : 'text-gray-900 group-hover:text-blue-600'} transition-colors`}>Multi registro</span>
                                <span className="text-sm text-gray-400">Se agregan múltiples filas por cada definición</span>
                            </div>
                        </div>
                        */}

                        {/* Render Mode Options (only if feature context exists) */}
                        {featureId && (
                            <>
                                <div
                                    className={`flex gap-3 items-start ${isReadonly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                                    onClick={() => !isReadonly && setRenderMode('tab')}
                                >
                                    <div className="mt-0.5">
                                        <div className={`w-8 h-5 rounded-full relative transition-colors duration-200 ease-in-out ${renderMode === 'tab' ? 'bg-[#0e79fd]' : 'bg-gray-200'}`}>
                                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ease-in-out shadow-sm ${renderMode === 'tab' ? 'transform translate-x-3' : ''}`} />
                                        </div>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className={`text-sm font-medium ${isReadonly ? 'text-gray-500' : 'text-gray-900 group-hover:text-blue-600'} transition-colors`}>Mostrar en pestaña</span>
                                        <span className="text-sm text-gray-400">Se creará una pestaña para los campos</span>
                                    </div>
                                </div>

                                <div
                                    className={`flex gap-3 items-start ${isReadonly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}
                                    onClick={() => !isReadonly && setRenderMode('inline')}
                                >
                                    <div className="mt-0.5">
                                        <div className={`w-8 h-5 rounded-full relative transition-colors duration-200 ease-in-out ${renderMode === 'inline' ? 'bg-[#0e79fd]' : 'bg-gray-200'}`}>
                                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ease-in-out shadow-sm ${renderMode === 'inline' ? 'transform translate-x-3' : ''}`} />
                                        </div>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className={`text-sm font-medium ${isReadonly ? 'text-gray-500' : 'text-gray-900 group-hover:text-blue-600'} transition-colors`}>Mostrar en línea</span>
                                        <span className="text-sm text-gray-400">Se agregarán los campos en el flujo</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Main Tabs Navigation */}
                <div className="flex gap-2 border-b border-gray-200 pb-1">
                    <button
                        className={`relative py-2 px-2 text-sm font-medium transition-colors ${activeTab === 'fields' ? 'text-[#0e79fd]' : 'text-gray-900 hover:text-gray-600'}`}
                        onClick={() => setActiveTab('fields')}
                    >
                        Campos
                        {activeTab === 'fields' && (
                            <div className="absolute bottom-[-5px] left-0 right-0 h-[2px] bg-[#0e79fd] rounded-full" />
                        )}
                    </button>
                    {/* Oculto por ahora:
                    {!entity.isPlansAndCriteria && (<button
                        className={`relative py-2 px-2 text-sm font-medium transition-colors ${activeTab === 'rules' ? 'text-[#0e79fd]' : 'text-gray-900 hover:text-gray-600'}`}
                        onClick={() => setActiveTab('rules')}
                    >
                        Reglas
                        {activeTab === 'rules' && (
                            <div className="absolute bottom-[-5px] left-0 right-0 h-[2px] bg-[#0e79fd] rounded-full" />
                        )}
                    </button>)}
                    */}
                </div>

                {/* Tab Content */}
                <div className="w-full min-h-[400px]">
                    {activeTab === 'fields' && (
                        <div className="flex w-full items-start gap-6">
                            {/* Tarjeta de la Lista (Izquierda) */}
                            <div
                                className={`bg-white rounded-2xl p-6 h-fit border border-transparent transition-all duration-300 ease-in-out origin-left ${editingField ? 'lg:w-[calc(65%-24px)]' : 'w-full'} shrink-0`}
                            >
                                <FieldDesigner
                                    fields={fields}
                                    isPlansAndCriteria={entity.isPlansAndCriteria}
                                    isReadonly={isReadonly}
                                    onChange={setFields}
                                    onEdit={setEditingField}
                                    onAdd={() => {
                                        if (isReadonly) return;
                                        const newField: any = {
                                            id: crypto.randomUUID(),
                                            code: `FIELD_${fields.length + 1}`,
                                            type: entity.isPlansAndCriteria ? 'GenericEntity' : 'Text',
                                            labels: { es: 'Nuevo Campo', en: 'New Field' },
                                            order: fields.length,
                                            layouts: entity.isPlansAndCriteria ? [{ breakpoint: 'md', colSpan: 2, rowSpan: 1, colStart: 0, rowStart: 0 }] : undefined,
                                            required: false,
                                            visible: true,
                                            readonly: false,
                                            isUnique: false
                                        };
                                        setEditingField(newField);
                                    }}
                                />
                            </div>

                            {/* Tarjeta del Editor (Derecha - Panel Deslizante) */}
                            {editingField && (
                                <div className="bg-white rounded-2xl p-6 h-fit border border-transparent w-full lg:w-[35%] shrink-0 animate-in slide-in-from-right-8 fade-in duration-300">
                                    <FieldEditor
                                        field={editingField}
                                        genericLists={entity.lists || []}
                                        isPlansAndCriteria={entity.isPlansAndCriteria}
                                        isReadonly={isReadonly}
                                        isNew={!fields.some(f => f.id === editingField.id)}
                                        onSave={(updated) => {
                                            setFields(prev => {
                                                const exists = prev.find(f => f.id === updated.id);
                                                if (exists) return prev.map(f => f.id === updated.id ? updated : f);
                                                return [...prev, updated];
                                            });
                                            setEditingField(null);
                                        }}
                                        onCancel={() => setEditingField(null)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {/* Oculto por ahora:
                    {activeTab === 'rules' && (
                        <RuleEditor
                            fields={fields}
                            rules={rules}
                            onSave={(updatedRules) => {
                                setRules(updatedRules);
                                setAlertConfig({
                                    isOpen: true,
                                    config: {
                                        title: 'Reglas actualizadas',
                                        description: 'Reglas actualizadas en memoria. Presiona "Guardar Cambios" para aplicarlas de forma permanente.',
                                        cancelText: 'Cerrar',
                                        onCancel: () => setAlertConfig({ isOpen: false, config: {} }),
                                        onConfirm: undefined,
                                    }
                                });
                            }}
                        />
                    )}
                    */}

                </div>
            </div>

            {alertConfig.isOpen && alertConfig.config.title && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <AlertDialog title={alertConfig.config.title} {...alertConfig.config} />
                </div>
            )}
        </LayoutContent>
    );
};
